import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Headphones, Volume2, Square, Trash2, Loader2, ChevronDown, ChevronUp, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

function formatDate(iso, lng) {
  try {
    return new Date(iso).toLocaleDateString(lng || "fr-FR", {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function MesPodcasts() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/podcasts")
      .then(({ data }) => setItems(data))
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => audioRef.current?.pause();
  }, []);

  const play = async (ep) => {
    if (playingId === ep.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    audioRef.current?.pause();
    setLoadingId(ep.id);
    try {
      const { data } = await api.post("/speak", { message: ep.script });
      const audio = new Audio(`data:audio/mp3;base64,${data.audio}`);
      audioRef.current = audio;
      audio.onended = () => setPlayingId(null);
      await audio.play();
      setPlayingId(ep.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("atelier.voiceHoarse"));
    } finally {
      setLoadingId(null);
    }
  };

  const remove = async (ep) => {
    setDeletingId(ep.id);
    try {
      await api.delete(`/podcasts/${ep.id}`);
      if (playingId === ep.id) { audioRef.current?.pause(); setPlayingId(null); }
      setItems((list) => list.filter((x) => x.id !== ep.id));
      toast.success(t("atelier.podcastDeleted"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("atelier.silent"));
    } finally {
      setDeletingId(null);
    }
  };

  const share = async (ep) => {
    const url = `${window.location.origin}/podcast/${ep.id}`;
    if (navigator.share) {
      try { await navigator.share({ title: ep.title, text: `${ep.title} · COCOON`, url }); return; } catch { /* fallthrough */ }
    }
    try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
    toast.success(t("echo.linkCopied"), { description: url });
  };


  return (
    <div className="h-full overflow-y-auto scroll-thin" data-testid="mes-podcasts">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <p className="text-primary/80 tracking-[0.25em] uppercase text-xs mb-4 text-center">Podcast</p>
        <h2 className="font-display text-gradient-orange text-center text-4xl sm:text-5xl italic mb-3">{t("atelier.podcasts")}</h2>
        <p className="text-foreground/50 max-w-lg mx-auto text-center mb-12 leading-relaxed">{t("atelier.podcastsSubtitle")}</p>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="animate-spin text-primary/60" size={28} />
          </div>
        ) : items.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center" data-testid="podcasts-empty">
            <Headphones className="mx-auto text-primary/50 mb-5" size={34} />
            <p className="font-display italic text-foreground/50 text-2xl max-w-md mx-auto">
              {t("atelier.podcastsEmpty")}
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            {items.map((ep, i) => {
              const open = openId === ep.id;
              const isPlaying = playingId === ep.id;
              const isLoading = loadingId === ep.id;
              return (
                <motion.div
                  key={ep.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  data-testid="podcast-card"
                  style={{ animationDelay: `${(i % 4) * 0.7}s` }}
                  className="glass frame-pulse rounded-2xl p-6"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center">
                      <Headphones className="text-primary" size={20} strokeWidth={1.8} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display text-xl text-foreground/90 truncate">{ep.title}</h3>
                      <p className="text-xs uppercase tracking-wider text-foreground/30 mt-1">
                        {formatDate(ep.created_at, i18n.language)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        data-testid={`podcast-play-${ep.id}`}
                        onClick={() => play(ep)}
                        disabled={isLoading}
                        className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs bg-primary/10 text-primary hover:bg-primary/20 transition-colors disabled:opacity-60"
                      >
                        {isLoading ? <Loader2 size={14} className="animate-spin" /> : isPlaying ? <Square size={13} /> : <Volume2 size={14} />}
                        {isPlaying ? t("atelier.silence") : t("atelier.ecouter")}
                      </button>
                      <button
                        data-testid={`podcast-share-${ep.id}`}
                        onClick={() => share(ep)}
                        className="p-2 rounded-full text-foreground/40 hover:text-primary hover:bg-primary/10 transition-colors"
                        title={t("atelier.podcastShare")}
                      >
                        <Share2 size={15} />
                      </button>
                      <button
                        data-testid={`podcast-delete-${ep.id}`}
                        onClick={() => remove(ep)}
                        disabled={deletingId === ep.id}
                        className="p-2 rounded-full text-foreground/40 hover:text-red-400 hover:bg-red-400/10 transition-colors disabled:opacity-60"
                        title={t("atelier.podcastDeleted")}
                      >
                        {deletingId === ep.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={15} />}
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={() => setOpenId(open ? null : ep.id)}
                    className="mt-4 flex items-center gap-2 text-sm text-foreground/50 hover:text-primary transition-colors text-left w-full"
                    data-testid={`podcast-toggle-${ep.id}`}
                  >
                    {open ? <ChevronUp size={15} className="flex-shrink-0" /> : <ChevronDown size={15} className="flex-shrink-0" />}
                    {!open && (
                      <span className="truncate italic text-foreground/40">{ep.script?.slice(0, 90)}…</span>
                    )}
                  </button>
                  {open && (
                    <p className="mt-3 font-display italic text-base text-foreground/70 leading-relaxed whitespace-pre-line">
                      {ep.script}
                    </p>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
